import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, Plus, Check, Trash, Clock, User, Factory, Buildings, HardHat, PencilRuler, MapPin,
  ListBullets, CalendarBlank, DotsSixVertical
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { RouteMapModal } from "@/components/RouteMapModal";
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getEntityIcon = (entityType, gabineteType) => {
  if (entityType === "client") return User;
  if (entityType === "gabinete") {
    if (gabineteType === "arquiteto") return PencilRuler;
    if (gabineteType === "construtor") return HardHat;
    return Buildings;
  }
  return Factory;
};

const TYPE_BADGE_DEFAULT = "bg-stone-200 text-stone-600";
const getTypeBadge = (type) => (type || "").trim().toLowerCase() === "visita" ? "bg-[#009392]/10 text-[#009392]" : TYPE_BADGE_DEFAULT;

function DroppableDayColumn({ day, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: day.date });
  return (
    <div
      ref={setNodeRef}
      className={`border rounded-xl overflow-hidden bg-white transition-colors ${isOver ? 'border-[#009392] ring-2 ring-[#009392]/30' : 'border-stone-200'}`}
      data-testid={`day-column-${day.date}`}
    >
      {children}
    </div>
  );
}

function SortableTaskCard({ task, isReadOnly, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled: isReadOnly });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`task-card-${task.id}`}>
      {children({ dragHandleProps: !isReadOnly ? { ...attributes, ...listeners } : {} })}
    </div>
  );
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function toISODate(d) {
  return d.toISOString().split("T")[0];
}

export function CalendarioSemanalTab({ isReadOnly }) {
  const [viewMode, setViewMode] = useState("semana"); // 'semana' | 'mes'
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [weekData, setWeekData] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [monthTasks, setMonthTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [routeDay, setRouteDay] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const [completeComment, setCompleteComment] = useState("");
  const [savingComplete, setSavingComplete] = useState(false);
  const [activeDragTask, setActiveDragTask] = useState(null);
  const originalDateRef = useRef(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  // Finds which day column (container id = day.date) currently holds a given task/day id
  const findContainer = (id) => {
    if (!weekData) return null;
    if (weekData.days.some((d) => d.date === id)) return id;
    for (const d of weekData.days) {
      if (d.tasks.some((t) => t.id === id)) return d.date;
    }
    return null;
  };

  const handleTaskDragStart = (event) => {
    const task = weekData?.days.flatMap((d) => d.tasks).find((t) => t.id === event.active.id);
    setActiveDragTask(task || null);
    originalDateRef.current = task?.scheduled_date || null;
  };

  const handleTaskDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    // Live-move the card into the day column being hovered, so it's clear where it will land -
    // this is only a local/visual move; the real persistence happens in handleTaskDragEnd.
    setWeekData((prev) => {
      const activeDay = prev.days.find((d) => d.date === activeContainer);
      const overDay = prev.days.find((d) => d.date === overContainer);
      const task = activeDay?.tasks.find((t) => t.id === active.id);
      if (!task) return prev;
      const overIndex = overDay.tasks.findIndex((t) => t.id === over.id);
      const insertAt = overIndex >= 0 ? overIndex : overDay.tasks.length;
      return {
        ...prev,
        days: prev.days.map((d) => {
          if (d.date === activeContainer) return { ...d, tasks: d.tasks.filter((t) => t.id !== active.id) };
          if (d.date === overContainer) {
            const newTasks = [...d.tasks];
            newTasks.splice(insertAt, 0, { ...task, scheduled_date: overContainer });
            return { ...d, tasks: newTasks };
          }
          return d;
        })
      };
    });
  };

  const handleTaskDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragTask(null);
    if (!over || !weekData) return;

    const container = findContainer(active.id);
    if (!container) return;
    const day = weekData.days.find((d) => d.date === container);
    const oldIndex = day.tasks.findIndex((t) => t.id === active.id);
    const overContainer = findContainer(over.id) || container;
    let newIndex = overContainer === container ? day.tasks.findIndex((t) => t.id === over.id) : oldIndex;
    if (newIndex === -1) newIndex = day.tasks.length - 1;

    let finalTasks = day.tasks;
    if (oldIndex !== newIndex) {
      finalTasks = arrayMove(day.tasks, oldIndex, newIndex);
      setWeekData((prev) => ({ ...prev, days: prev.days.map((d) => (d.date === container ? { ...d, tasks: finalTasks } : d)) }));
    }

    const dateChanged = container !== originalDateRef.current;
    const orderChanged = oldIndex !== newIndex;
    if (!dateChanged && !orderChanged) return;

    const task = day.tasks[oldIndex];
    try {
      if (dateChanged) {
        await axios.put(`${API}/tasks/${task.id}`, {
          client_id: task.client_id || null,
          gabinete_id: task.gabinete_id || null,
          custom_name: task.custom_name || null,
          task_type: task.task_type,
          scheduled_date: container,
          scheduled_time: task.scheduled_time || null,
          notes: task.notes || null,
          reminder_minutes: task.reminder_minutes,
          status: task.status
        });
      }
      // Persist the manual order within the destination day (covers cross-day drop position
      // and same-day reordering in one call).
      await axios.put(`${API}/tasks/day/${container}/reorder`, { ordered_ids: finalTasks.map((t) => t.id) });
      if (dateChanged) toast.success("Tarefa movida");
    } catch (error) {
      toast.error("Erro ao mover a tarefa");
      fetchWeek();
    }
  };

  const fetchWeek = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tasks/week`, { params: { start_date: toISODate(weekStart) } });
      setWeekData(res.data);
    } catch (error) {
      toast.error("Erro ao carregar agenda semanal");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonth = async () => {
    setLoading(true);
    try {
      const y = monthAnchor.getFullYear(), m = monthAnchor.getMonth();
      const dateFrom = toISODate(new Date(y, m, 1));
      const dateTo = toISODate(new Date(y, m + 1, 0));
      const res = await axios.get(`${API}/tasks`, { params: { date_from: dateFrom, date_to: dateTo } });
      setMonthTasks(res.data);
    } catch (error) {
      toast.error("Erro ao carregar agenda mensal");
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => viewMode === "semana" ? fetchWeek() : fetchMonth();

  useEffect(() => { if (viewMode === "semana") fetchWeek(); }, [weekStart, viewMode]);
  useEffect(() => { if (viewMode === "mes") fetchMonth(); }, [monthAnchor, viewMode]);

  const changeWeek = (deltaWeeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaWeeks * 7);
    setWeekStart(d);
  };

  const changeMonth = (deltaMonths) => {
    const d = new Date(monthAnchor);
    d.setMonth(d.getMonth() + deltaMonths);
    setMonthAnchor(d);
  };

  const buildMonthGrid = () => {
    const y = monthAnchor.getFullYear(), m = monthAnchor.getMonth();
    const firstDay = new Date(y, m, 1);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    let startWeekday = firstDay.getDay();
    startWeekday = startWeekday === 0 ? 6 : startWeekday - 1; // Monday = 0
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toISODate(new Date(y, m, d));
      cells.push({
        date: dateStr,
        dayNum: d,
        tasks: monthTasks.filter(t => t.scheduled_date === dateStr).sort((a, b) => (a.scheduled_time || "99:99").localeCompare(b.scheduled_time || "99:99"))
      });
    }
    return cells;
  };

  const monthLabel = (() => {
    const label = monthAnchor.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
  })();

  const openNewTask = (date) => {
    setEditingTask(null);
    setPrefillDate(date);
    setShowDialog(true);
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setPrefillDate(null);
    setShowDialog(true);
  };

  const completeTask = async (taskId, comments) => {
    setSavingComplete(true);
    try {
      await axios.patch(`${API}/tasks/${taskId}/complete`, { comments: comments || null });
      toast.success("Visita concluída e comentários guardados no histórico");
      setCompletingTask(null);
      setCompleteComment("");
      refresh();
    } catch {
      toast.error("Erro ao concluir tarefa");
    } finally {
      setSavingComplete(false);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success("Tarefa eliminada");
      refresh();
    } catch {
      toast.error("Erro ao eliminar tarefa");
    } finally {
      setDeleteTaskId(null);
    }
  };

  const weekLabel = weekData
    ? `${new Date(weekData.week_start + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })} - ${new Date(weekData.week_end + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}`
    : "";

  return (
    <div data-testid="calendario-semanal-tab">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          {viewMode === "semana" ? (
            <>
              <button onClick={() => changeWeek(-1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-prev-week">
                <CaretLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-stone-700 min-w-[120px] sm:min-w-[160px] text-center" data-testid="week-range-label">{weekLabel}</span>
              <button onClick={() => changeWeek(1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-next-week">
                <CaretRight size={18} />
              </button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(getMonday(new Date()))} data-testid="btn-today-week">
                Hoje
              </Button>
            </>
          ) : (
            <>
              <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-prev-month">
                <CaretLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-stone-700 min-w-[120px] sm:min-w-[160px] text-center" data-testid="month-range-label">{monthLabel}</span>
              <button onClick={() => changeMonth(1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-next-month">
                <CaretRight size={18} />
              </button>
              <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setDate(1); setMonthAnchor(d); }} data-testid="btn-today-month">
                Hoje
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-stone-100 rounded-lg p-1" data-testid="calendario-view-toggle">
            <button
              onClick={() => setViewMode("semana")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "semana" ? "bg-white text-[#009392] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
              data-testid="btn-view-semana"
            >
              <ListBullets size={16} /> Semana
            </button>
            <button
              onClick={() => setViewMode("mes")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "mes" ? "bg-white text-[#009392] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
              data-testid="btn-view-mes"
            >
              <CalendarBlank size={16} /> Mês
            </button>
          </div>
          <Button className="btn-primary" onClick={() => openNewTask(viewMode === "semana" ? toISODate(weekStart) : toISODate(monthAnchor))} data-testid="btn-new-task">
            <Plus size={16} /> Nova Tarefa
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 text-center py-8">A carregar...</p>
      ) : viewMode === "mes" ? (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="calendario-month-grid">
          <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
            {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-stone-500 py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {buildMonthGrid().map((cell, idx) => (
              <div
                key={idx}
                className={`border-b border-r border-stone-100 min-h-[110px] p-1.5 ${cell && cell.date === toISODate(new Date()) ? 'bg-[#009392]/5' : ''}`}
                data-testid={cell ? `month-day-${cell.date}` : undefined}
              >
                {cell && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-xs font-semibold ${cell.date === toISODate(new Date()) ? 'text-[#009392]' : 'text-stone-500'}`}>{cell.dayNum}</p>
                      <button onClick={() => openNewTask(cell.date)} className="p-0.5 hover:bg-stone-200 rounded" data-testid={`btn-add-task-month-${cell.date}`}>
                        <Plus size={11} />
                      </button>
                    </div>
                    <div className="space-y-1 max-h-[90px] overflow-y-auto">
                      {cell.tasks.map((task) => {
                        const Icon = getEntityIcon(task.entity_type, task.gabinete_type);
                        return (
                          <div
                            key={task.id}
                            onClick={() => openEditTask(task)}
                            className={`flex items-center gap-1 px-1.5 py-1 rounded cursor-pointer group ${task.status === 'concluida' ? 'bg-stone-50 opacity-60' : 'bg-stone-50 hover:bg-[#009392]/10'}`}
                            data-testid={`month-task-${task.id}`}
                            title={task.entity_name}
                          >
                            <Icon size={11} className="text-stone-400 flex-shrink-0" />
                            <span className="text-[11px] text-stone-700 truncate flex-1">{task.entity_name}</span>
                            {task.status !== 'concluida' && (
                              <button onClick={(e) => { e.stopPropagation(); setCompletingTask(task); setCompleteComment(""); }} className="opacity-0 group-hover:opacity-100 text-green-500 flex-shrink-0">
                                <Check size={11} />
                              </button>
                            )}
                            {!isReadOnly && (
                              <button onClick={(e) => { e.stopPropagation(); setDeleteTaskId(task.id); }} className="opacity-0 group-hover:opacity-100 text-red-400 flex-shrink-0">
                                <Trash size={11} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragStart={handleTaskDragStart} onDragOver={handleTaskDragOver} onDragEnd={handleTaskDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {weekData?.days.map((day) => (
              <DroppableDayColumn key={day.date} day={day}>
                <div className="bg-stone-50 px-3 py-2 border-b border-stone-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-[#009392]">{day.weekday_label}</p>
                    <p className="text-sm font-bold text-stone-800">
                      {new Date(day.date + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}
                    </p>
                  </div>
                  <button onClick={() => openNewTask(day.date)} className="p-1.5 hover:bg-stone-200 rounded-lg" data-testid={`btn-add-task-${day.date}`}>
                    <Plus size={14} />
                  </button>
                </div>
                {day.tasks.length > 0 && (
                  <button
                    onClick={() => setRouteDay(day)}
                    className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium text-[#009392] bg-[#009392]/5 hover:bg-[#009392]/10 py-1.5 border-b border-stone-200"
                    data-testid={`btn-day-route-${day.date}`}
                  >
                    <MapPin size={12} /> Ver rota do dia
                  </button>
                )}
                <SortableContext items={day.tasks.map((t) => t.id)}>
                <div className="p-2 space-y-1.5 min-h-[120px]">
                  {day.tasks.length === 0 && <p className="text-xs text-stone-400 text-center py-4">Sem tarefas</p>}
                  {day.tasks.map((task) => {
                    const Icon = getEntityIcon(task.entity_type, task.gabinete_type);
                    return (
                      <SortableTaskCard key={task.id} task={task} isReadOnly={isReadOnly}>
                        {({ dragHandleProps }) => (
                          <div className={`p-2 rounded-lg border ${task.status === 'concluida' ? 'bg-stone-50 border-stone-200 opacity-60' : 'bg-white border-stone-200'}`}>
                            <div className="flex items-start justify-between gap-1">
                              <button onClick={() => openEditTask(task)} className="flex-1 text-left min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <Icon size={12} className="text-stone-400 flex-shrink-0" />
                                  <span className="text-xs font-medium text-stone-800 truncate">{task.entity_name}</span>
                                </div>
                                {task.entity_city && (
                                  <div className="flex items-center gap-0.5 mt-0.5 text-[10px] text-[#009392] font-medium" data-testid={`task-branch-city-${task.id}`}>
                                    <MapPin size={9} className="flex-shrink-0" />
                                    <span className="truncate">{task.entity_city}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 mt-1">
                                  {task.scheduled_time && <span className="text-[10px] text-stone-400 flex items-center gap-0.5"><Clock size={10} />{task.scheduled_time}</span>}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium max-w-[110px] truncate ${getTypeBadge(task.task_type)}`} title={task.task_type}>
                                    {task.task_type}
                                  </span>
                                </div>
                              </button>
                              <div className="flex gap-0.5 flex-shrink-0 items-center">
                                {!isReadOnly && (
                                  <button
                                    {...dragHandleProps}
                                    className="p-1 text-stone-300 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none"
                                    data-testid={`drag-handle-task-${task.id}`}
                                    title="Arrastar para reordenar ou mudar de dia"
                                  >
                                    <DotsSixVertical size={12} />
                                  </button>
                                )}
                                {task.status !== 'concluida' && (
                                  <button onClick={() => { setCompletingTask(task); setCompleteComment(""); }} className="p-1 text-green-500 hover:bg-green-50 rounded" data-testid={`btn-complete-task-${task.id}`}>
                                    <Check size={12} />
                                  </button>
                                )}
                                {!isReadOnly && (
                                  <button onClick={() => setDeleteTaskId(task.id)} className="p-1 text-red-400 hover:bg-red-50 rounded" data-testid={`btn-delete-task-${task.id}`}>
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </SortableTaskCard>
                    );
                  })}
                </div>
                </SortableContext>
              </DroppableDayColumn>
            ))}
          </div>
          <DragOverlay>
            {activeDragTask ? (
              <div className="p-2 rounded-lg border bg-white border-[#009392] shadow-lg w-48">
                <div className="flex items-center gap-1.5">
                  {(() => { const Icon = getEntityIcon(activeDragTask.entity_type, activeDragTask.gabinete_type); return <Icon size={12} className="text-stone-400 flex-shrink-0" />; })()}
                  <span className="text-xs font-medium text-stone-800 truncate">{activeDragTask.entity_name}</span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskFormDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={refresh}
        task={editingTask}
        defaultDate={prefillDate}
      />

      <ConfirmDeleteDialog
        open={!!deleteTaskId}
        onOpenChange={(v) => !v && setDeleteTaskId(null)}
        onConfirm={() => deleteTask(deleteTaskId)}
        itemType="tarefa"
      />

      <RouteMapModal
        open={!!routeDay}
        onClose={() => setRouteDay(null)}
        dayLabel={routeDay ? `${routeDay.weekday_label}, ${new Date(routeDay.date + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}` : ""}
        tasks={routeDay?.tasks || []}
      />

      <Dialog open={!!completingTask} onOpenChange={(v) => !v && setCompletingTask(null)}>
        <DialogContent className="sm:max-w-md" data-testid="complete-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Concluir visita</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500 -mt-2">
            {completingTask?.entity_name} - {completingTask?.task_type}
          </p>
          <div>
            <label className="text-sm font-medium text-stone-700">Comentários da visita</label>
            <Textarea
              value={completeComment}
              onChange={(e) => setCompleteComment(e.target.value)}
              placeholder="O que aconteceu nesta visita? (fica guardado no histórico do cliente)"
              className="gresart-input mt-1 resize-y min-h-[100px]"
              autoFocus
              data-testid="complete-task-comment-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setCompletingTask(null)} data-testid="btn-cancel-complete-task">
              Cancelar
            </Button>
            <Button
              type="button"
              className="btn-primary"
              disabled={savingComplete}
              onClick={() => completeTask(completingTask.id, completeComment)}
              data-testid="btn-save-complete-task"
            >
              {savingComplete ? "A guardar..." : "Guardar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
